import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  IRowNode,
  IServerSideSelectionState,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { WithLocalStorage } from '@/components';
import useDebounce from '@/components/hooks/useDebounce';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { useAccessToken } from '@/services/auth';
import { PrimaryContext } from '@/services/context';
import {
  type MatingsSourceType,
  onFarmClientOnFarmSearchTotalsOptions,
  type OnFarmImplantEmbryoPick,
  onFarmImplantRecordIdsOptions,
  OnFarmImplantRequestError,
  type OnFarmImplantShipmentContents,
  type OnFarmImplantShipmentSummary,
  toRecordKey,
} from '@/services/on-farm-implant';
import { useContextMenu } from '@/utils/context-menu';

import { useOnFarmImplantDataSource } from '../../hooks/useOnFarmImplantDataSource';
import type { LoadedSource, MatingRecord } from '../../types';
import { getLoadedSourceColumnDefs } from '../../utils/columnDefs';
import {
  type PickedShipment,
  type ShipmentAddResult,
  type ShipmentInSelection,
  toEmbryoPickKey,
  toEmbryoPickMatingKey,
} from '../../utils/shipmentSelection';
import { ClientOnFarmEmbryoDetail } from './ClientOnFarmEmbryoDetail';
import { ClientOnFarmMatingCheckbox } from './ClientOnFarmSelection';
import {
  CLIENT_ON_FARM_SOURCE_ID,
  ClientOnFarmSelectionProvider,
  useClientOnFarmSelection,
} from './clientOnFarmSelectionContext';
import { FrozenPartialMatingCell } from './FrozenPartialMatingCell';
import {
  FROZEN_SOURCE_ID,
  FrozenPartialMatingsProvider,
} from './frozenPartialMatingsContext';
import { ShipmentPicker } from './ShipmentPicker';
import { ShipmentsInSelection } from './ShipmentsInSelection';

const PAGE_SIZE = 20;

const SHIPMENT_TOAST_TIMEOUT = 8000;

const NO_EMBRYO_PICKS: OnFarmImplantEmbryoPick[] = [];

const NO_SHIPMENTS: ShipmentInSelection[] = [];

const NO_PARTIAL_KEYS: ReadonlySet<string> = new Set();

const SHIPMENT_SOURCE_IDS = new Set<string>(['fresh', FROZEN_SOURCE_ID]);

const GRID_SELECT_ALL_SOURCES = new Set<string>([
  'uiSelectAll',
  'uiSelectAllCurrentPage',
  'uiSelectAllFiltered',
]);

const SOURCE_TYPE_MAP: Record<string, MatingsSourceType> = {
  fresh: 'fresh',
  frozen: 'frozen',
  flush: 'flush',
  'client-on-farm-tog': 'client-on-farm',
};

const getNodeRecordKey = (node: IRowNode): string | null => {
  const record = node.data as MatingRecord | undefined;
  return record?.id ? toRecordKey(record.id, record.accountId) : null;
};

const searchStorageKey = (sessionId: string, sourceId: string) =>
  `on-farm-implant-source-search:${sessionId}:${sourceId}`;

const accountSearchStorageId = (sourceId: string) => `${sourceId}:account`;

const allAccountsStorageId = (sourceId: string) => `${sourceId}:all-accounts`;

const readStoredSearch = (
  sessionId: string | null | undefined,
  sourceId: string,
) => {
  if (!sessionId) return '';
  try {
    return sessionStorage.getItem(searchStorageKey(sessionId, sourceId)) ?? '';
  } catch {
    return '';
  }
};

const writeStoredSearch = (
  sessionId: string | null | undefined,
  sourceId: string,
  value: string,
) => {
  if (!sessionId) return;
  try {
    const key = searchStorageKey(sessionId, sourceId);
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    return;
  }
};

type LoadedSourceCardProps = {
  source: LoadedSource;
  index: number;
  siteId: string;
  accountId?: string;
  accountName?: string | null;
  sessionId?: string | null;
  selectedRecordKeys: string[];
  embryoPicks?: OnFarmImplantEmbryoPick[];
  shipments?: ShipmentInSelection[];
  onAddShipment?: (
    summary: OnFarmImplantShipmentSummary,
    contents: OnFarmImplantShipmentContents,
  ) => ShipmentAddResult;
  onUndoShipment?: (result: ShipmentAddResult) => void;
  onRemoveShipment?: (shipment: PickedShipment) => void;
  onSelectedRecordKeysChange: (sourceId: string, recordKeys: string[]) => void;
  readOnly?: boolean;
  onRemove: (sourceId: string) => void;
};

export const LoadedSourceCard = ({
  source,
  index,
  siteId,
  accountId,
  accountName,
  sessionId,
  selectedRecordKeys,
  embryoPicks = NO_EMBRYO_PICKS,
  shipments = NO_SHIPMENTS,
  onAddShipment,
  onUndoShipment,
  onRemoveShipment,
  onSelectedRecordKeysChange,
  readOnly = false,
  onRemove,
}: LoadedSourceCardProps) => {
  const token = useAccessToken();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const gridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { getContextMenuItems } = useContextMenu({ idKey: 'id' });
  const gridRef = useRef<AgGridReact>(null);
  const isApplyingSelectionRef = useRef(false);

  const [searchText, setSearchText] = useState(() =>
    readStoredSearch(sessionId, source.sourceId),
  );
  const [accountSearchText, setAccountSearchText] = useState(() =>
    readStoredSearch(sessionId, accountSearchStorageId(source.sourceId)),
  );
  const [filterByCurrentAccount, setFilterByCurrentAccount] = useState(
    () => !readStoredSearch(sessionId, allAccountsStorageId(source.sourceId)),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [selectedOnlyKeys, setSelectedOnlyKeys] = useState<string[] | null>(
    null,
  );
  const [selectedOnlyPartialKeys, setSelectedOnlyPartialKeys] =
    useState<ReadonlySet<string>>(NO_PARTIAL_KEYS);
  const showSelectedOnly = selectedOnlyKeys !== null;
  const selectedOnlyRecordKeys = useMemo(
    () =>
      selectedOnlyKeys?.filter((key) => !selectedOnlyPartialKeys.has(key)) ??
      null,
    [selectedOnlyKeys, selectedOnlyPartialKeys],
  );
  const debouncedSearch = useDebounce(searchText, 300);
  const debouncedAccountSearch = useDebounce(accountSearchText, 300);

  const apiSourceType = SOURCE_TYPE_MAP[source.sourceId];
  const isClientOnFarm = source.sourceId === CLIENT_ON_FARM_SOURCE_ID;
  const isFrozen = source.sourceId === FROZEN_SOURCE_ID;
  const searchTerm = isClientOnFarm ? debouncedSearch.trim() : debouncedSearch;
  const usesCurrentAccount =
    !isClientOnFarm || (filterByCurrentAccount && !!accountId);
  const scopedAccountId = usesCurrentAccount ? accountId : undefined;
  const accountSearchTerm =
    isClientOnFarm && !usesCurrentAccount ? debouncedAccountSearch.trim() : '';

  const selectedRecordKeysRef = useRef(selectedRecordKeys);

  const visibleKeys = useMemo(
    () => (selectedOnlyKeys ? new Set(selectedOnlyKeys) : undefined),
    [selectedOnlyKeys],
  );

  const clientOnFarmSelection = useClientOnFarmSelection({
    sourceId: source.sourceId,
    selectedRecordKeys,
    onSelectedRecordKeysChange,
    search: showSelectedOnly ? undefined : searchTerm || undefined,
    visibleKeys,
    readOnly,
  });

  const filters = useMemo(
    () =>
      selectedOnlyKeys
        ? {
            siteId,
            searchTerm: isClientOnFarm ? undefined : searchTerm || undefined,
            selectedRecordKeys: selectedOnlyKeys,
          }
        : {
            siteId,
            accountId: scopedAccountId,
            searchTerm: searchTerm || undefined,
            accountSearch: accountSearchTerm || undefined,
          },
    [
      accountSearchTerm,
      isClientOnFarm,
      scopedAccountId,
      searchTerm,
      selectedOnlyKeys,
      siteId,
    ],
  );

  const clientOnFarmSearch = isClientOnFarm ? searchTerm : '';
  const hasClientOnFarmSearch = !!clientOnFarmSearch || !!accountSearchTerm;
  const { data: clientOnFarmSearchTotals } = useQuery({
    ...onFarmClientOnFarmSearchTotalsOptions(token, {
      accountId: scopedAccountId,
      search: clientOnFarmSearch || undefined,
      accountSearch: accountSearchTerm || undefined,
    }),
    enabled: !!token && hasClientOnFarmSearch,
  });

  const showClientOnFarmSearchHint =
    isClientOnFarm &&
    !showSelectedOnly &&
    !scopedAccountId &&
    !hasClientOnFarmSearch;

  const displayTotals = showClientOnFarmSearchHint
    ? { totalRecords: 0, totalEmbryos: 0 }
    : hasClientOnFarmSearch && clientOnFarmSearchTotals
      ? clientOnFarmSearchTotals
      : source;

  const applySelectionToGrid = useCallback(() => {
    if (isClientOnFarm) return;
    const api = gridRef.current?.api;
    if (!api) return;

    const selected = new Set(selectedRecordKeysRef.current);
    const toSelect: IRowNode[] = [];
    const toDeselect: IRowNode[] = [];

    api.forEachNode((node) => {
      const recordKey = getNodeRecordKey(node);
      if (!recordKey) return;
      const shouldSelect = selected.has(recordKey);
      if (node.isSelected() === shouldSelect) return;
      (shouldSelect ? toSelect : toDeselect).push(node);
    });

    if (toSelect.length === 0 && toDeselect.length === 0) return;

    isApplyingSelectionRef.current = true;
    try {
      if (toDeselect.length > 0) {
        api.setNodesSelected({
          nodes: toDeselect,
          newValue: false,
          source: 'api',
        });
      }
      if (toSelect.length > 0) {
        api.setNodesSelected({
          nodes: toSelect,
          newValue: true,
          source: 'api',
        });
      }
    } finally {
      isApplyingSelectionRef.current = false;
    }
  }, [isClientOnFarm]);

  const { dataSource } = useOnFarmImplantDataSource(
    apiSourceType,
    filters,
    setIsLoading,
    applySelectionToGrid,
  );

  useEffect(() => {
    gridRef.current?.api?.refreshServerSide({ purge: true });
  }, [filters]);

  useEffect(() => {
    selectedRecordKeysRef.current = selectedRecordKeys;
    applySelectionToGrid();
  }, [applySelectionToGrid, selectedRecordKeys]);

  const canLoadClientOnFarmScope =
    isClientOnFarm && !!apiSourceType && !!siteId;

  const clientOnFarmScopeOptions = useMemo(
    () =>
      onFarmImplantRecordIdsOptions(token, {
        sourceType: apiSourceType ?? 'client-on-farm',
        siteId,
        accountId: scopedAccountId,
        search: searchTerm || undefined,
        accountSearch: accountSearchTerm || undefined,
      }),
    [
      accountSearchTerm,
      apiSourceType,
      scopedAccountId,
      searchTerm,
      siteId,
      token,
    ],
  );

  const { data: clientOnFarmScope } = useQuery({
    ...clientOnFarmScopeOptions,
    enabled:
      !!token &&
      canLoadClientOnFarmScope &&
      !showClientOnFarmSearchHint &&
      !showSelectedOnly,
  });

  const clientOnFarmScopeKeys = useMemo(
    () =>
      (clientOnFarmScope?.records ?? []).map((record) =>
        toRecordKey(record.recordId, record.accountId),
      ),
    [clientOnFarmScope],
  );

  const { setEmbryosSelected } = clientOnFarmSelection;

  const handleClientOnFarmSelectAllChange = useCallback(
    async (checked: boolean) => {
      if (!canLoadClientOnFarmScope) return;
      setIsSelectingAll(true);
      try {
        const response = await queryClient.fetchQuery(clientOnFarmScopeOptions);
        setEmbryosSelected(
          response.records.map((record) =>
            toRecordKey(record.recordId, record.accountId),
          ),
          checked,
        );
      } catch (error) {
        const detail =
          error instanceof OnFarmImplantRequestError && error.problems.length
            ? error.problems.join(' ')
            : undefined;
        toast.add({
          data: { variant: 'error' },
          title: t('onFarmImplant.sourceCard.selectAllFailed'),
          description: detail,
        });
      } finally {
        setIsSelectingAll(false);
      }
    },
    [
      canLoadClientOnFarmScope,
      clientOnFarmScopeOptions,
      queryClient,
      setEmbryosSelected,
      t,
    ],
  );

  const handleSelectAllChange = useCallback(
    async (checked: boolean) => {
      if (selectedOnlyKeys && selectedOnlyRecordKeys) {
        if (isClientOnFarm) {
          setEmbryosSelected(selectedOnlyKeys, checked);
          return;
        }
        const snapshot = new Set(selectedOnlyRecordKeys);
        const remaining = selectedRecordKeysRef.current.filter(
          (key) => !snapshot.has(key),
        );
        onSelectedRecordKeysChange(
          source.sourceId,
          checked ? [...remaining, ...selectedOnlyRecordKeys] : remaining,
        );
        return;
      }
      if (isClientOnFarm) {
        await handleClientOnFarmSelectAllChange(checked);
        return;
      }
      if (!checked) {
        onSelectedRecordKeysChange(source.sourceId, []);
        return;
      }
      if (!apiSourceType || !siteId) return;

      setIsSelectingAll(true);
      try {
        const response = await queryClient.fetchQuery(
          onFarmImplantRecordIdsOptions(token, {
            sourceType: apiSourceType,
            siteId,
            accountId,
            search: searchTerm || undefined,
          }),
        );
        onSelectedRecordKeysChange(
          source.sourceId,
          response.records.map((record) =>
            toRecordKey(record.recordId, record.accountId),
          ),
        );
      } catch (error) {
        gridRef.current?.api?.deselectAll('api');
        applySelectionToGrid();
        const detail =
          error instanceof OnFarmImplantRequestError && error.problems.length
            ? error.problems.join(' ')
            : undefined;
        toast.add({
          data: { variant: 'error' },
          title: t('onFarmImplant.sourceCard.selectAllFailed'),
          description: detail,
        });
      } finally {
        setIsSelectingAll(false);
      }
    },
    [
      accountId,
      apiSourceType,
      applySelectionToGrid,
      handleClientOnFarmSelectAllChange,
      isClientOnFarm,
      onSelectedRecordKeysChange,
      queryClient,
      searchTerm,
      selectedOnlyKeys,
      selectedOnlyRecordKeys,
      setEmbryosSelected,
      siteId,
      source.sourceId,
      t,
      token,
    ],
  );

  const embryoPickMatingCounts = useMemo(() => {
    const counts = new Map<string, number>();
    if (!isFrozen) return counts;
    embryoPicks.forEach((pick) => {
      const matingKey = toEmbryoPickMatingKey(pick);
      counts.set(matingKey, (counts.get(matingKey) ?? 0) + 1);
    });
    return counts;
  }, [embryoPicks, isFrozen]);

  const selectedKeySet = useMemo(
    () => new Set(selectedRecordKeys),
    [selectedRecordKeys],
  );

  const partialMatings = useMemo(() => {
    const partial = new Map<string, number>();
    embryoPickMatingCounts.forEach((count, matingKey) => {
      if (!selectedKeySet.has(matingKey)) partial.set(matingKey, count);
    });
    return partial;
  }, [embryoPickMatingCounts, selectedKeySet]);

  const handleShowSelectedOnlyChange = useCallback(
    (checked: boolean) => {
      setSelectedOnlyKeys(
        checked
          ? [...selectedRecordKeysRef.current, ...partialMatings.keys()]
          : null,
      );
      setSelectedOnlyPartialKeys(
        checked ? new Set(partialMatings.keys()) : NO_PARTIAL_KEYS,
      );
    },
    [partialMatings],
  );

  const supportsShipments =
    SHIPMENT_SOURCE_IDS.has(source.sourceId) && !!onAddShipment;

  const embryoPickKeys = useMemo(
    () => new Set(embryoPicks.map(toEmbryoPickKey)),
    [embryoPicks],
  );

  const flashRows = useCallback((recordKeys: string[]) => {
    const api = gridRef.current?.api;
    if (!api || recordKeys.length === 0) return;
    const rowNodes = recordKeys
      .map((key) => api.getRowNode(key))
      .filter((node): node is IRowNode => !!node);
    if (rowNodes.length > 0) api.flashCells({ rowNodes });
  }, []);

  const handleAddShipment = useCallback(
    (
      summary: OnFarmImplantShipmentSummary,
      contents: OnFarmImplantShipmentContents,
    ) => {
      if (!onAddShipment) return;
      const result = onAddShipment(summary, contents);
      flashRows(result.rowKeys);
      const detailKey = isFrozen
        ? 'onFarmImplant.sourceCard.shipments.addedFrozen'
        : 'onFarmImplant.sourceCard.shipments.addedFresh';
      const toastId = toast.add({
        data: {
          variant: result.addedKeys.length > 0 ? 'success' : 'info',
        },
        title: t('onFarmImplant.sourceCard.shipments.added', {
          shipment: summary.shipmentNumber,
        }),
        description: t(detailKey, {
          added: result.addedKeys.length,
          already: result.alreadySelected,
        }),
        timeout: SHIPMENT_TOAST_TIMEOUT,
        actionProps:
          result.addedKeys.length > 0 && onUndoShipment
            ? {
                children: t('onFarmImplant.sourceCard.shipments.undo'),
                onClick() {
                  onUndoShipment(result);
                  toast.close(toastId);
                },
              }
            : undefined,
      });
    },
    [flashRows, isFrozen, onAddShipment, onUndoShipment, t],
  );

  const handleRemoveShipment = useCallback(
    (shipment: ShipmentInSelection) => {
      onRemoveShipment?.(shipment);
    },
    [onRemoveShipment],
  );

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      if (isClientOnFarm) return;
      if (isApplyingSelectionRef.current) return;
      if (event.source === 'api') return;

      if (GRID_SELECT_ALL_SOURCES.has(event.source)) {
        const state =
          event.api.getServerSideSelectionState() as IServerSideSelectionState | null;
        void handleSelectAllChange(state?.selectAll ?? false);
        return;
      }

      const loadedKeys = new Set<string>();
      const nextSelected = new Set<string>();
      event.api.forEachNode((node) => {
        const recordKey = getNodeRecordKey(node);
        if (!recordKey) return;
        loadedKeys.add(recordKey);
        if (node.isSelected()) {
          nextSelected.add(recordKey);
        }
      });

      const retained = selectedRecordKeysRef.current.filter(
        (key) => !loadedKeys.has(key),
      );
      onSelectedRecordKeysChange(source.sourceId, [
        ...retained,
        ...nextSelected,
      ]);
    },
    [
      handleSelectAllChange,
      isClientOnFarm,
      onSelectedRecordKeysChange,
      source.sourceId,
    ],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
      writeStoredSearch(sessionId, source.sourceId, e.target.value);
    },
    [sessionId, source.sourceId],
  );

  const handleAccountSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAccountSearchText(e.target.value);
      writeStoredSearch(
        sessionId,
        accountSearchStorageId(source.sourceId),
        e.target.value,
      );
    },
    [sessionId, source.sourceId],
  );

  const handleFilterByCurrentAccountChange = useCallback(
    (checked: boolean) => {
      setFilterByCurrentAccount(checked);
      writeStoredSearch(
        sessionId,
        allAccountsStorageId(source.sourceId),
        checked ? '' : '1',
      );
    },
    [sessionId, source.sourceId],
  );

  const columnDefs = useMemo(() => {
    const defs = getLoadedSourceColumnDefs(t, {
      showShipment: SHIPMENT_SOURCE_IDS.has(source.sourceId),
    });
    if (isClientOnFarm) {
      return defs.map((def) => {
        if (def.field === 'checkbox') {
          return {
            ...def,
            width: 44,
            checkboxSelection: false,
            headerCheckboxSelection: false,
            cellRenderer: ClientOnFarmMatingCheckbox,
          };
        }
        if (def.field === 'recordName') {
          return {
            ...def,
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: { innerRenderer: def.cellRenderer },
          };
        }
        return def;
      });
    }
    if (isFrozen) {
      return defs.map((def) => {
        if (def.field === 'recordName') {
          return {
            ...def,
            cellRenderer: FrozenPartialMatingCell,
            cellRendererParams: { innerRenderer: def.cellRenderer },
          };
        }
        if (readOnly && def.field === 'checkbox') {
          return {
            ...def,
            checkboxSelection: false,
            headerCheckboxSelection: false,
          };
        }
        return def;
      });
    }
    if (!readOnly) return defs;
    return defs.map((def) =>
      def.field === 'checkbox'
        ? { ...def, checkboxSelection: false, headerCheckboxSelection: false }
        : def,
    );
  }, [isClientOnFarm, isFrozen, readOnly, source.sourceId, t]);

  const handSelectedCount = selectedRecordKeys.length;
  const selectedCount = handSelectedCount + partialMatings.size;
  const selectableTotal = isClientOnFarm
    ? Math.max(displayTotals.totalEmbryos, selectedCount)
    : isFrozen
      ? Math.max(source.totalRecords, selectedCount)
      : source.totalRecords;
  const allSelected = selectedOnlyKeys
    ? isClientOnFarm
      ? selectedOnlyKeys.length > 0 &&
        selectedOnlyKeys.every((key) =>
          clientOnFarmSelection.selectedKeys.has(key),
        )
      : (selectedOnlyRecordKeys ?? []).length > 0 &&
        (selectedOnlyRecordKeys ?? []).every((key) => selectedKeySet.has(key))
    : isClientOnFarm
      ? clientOnFarmScopeKeys.length > 0 &&
        clientOnFarmScopeKeys.every((key) =>
          clientOnFarmSelection.selectedKeys.has(key),
        )
      : source.totalRecords > 0 && handSelectedCount >= source.totalRecords;
  const someSelected = selectedCount > 0 && !allSelected;

  const masterDetailProps = isClientOnFarm
    ? {
        masterDetail: true,
        isRowMaster: () => true,
        detailCellRenderer: ClientOnFarmEmbryoDetail,
        detailRowAutoHeight: true,
      }
    : {};

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <i
            data-drag-handle
            className="pi pi-bars cursor-grab text-sm text-gray-400"
          />
          <div className="flex size-6 items-center justify-center rounded bg-gray-100 text-xs font-bold text-gray-600">
            {index + 1}
          </div>
          <span className="text-sm">{source.icon}</span>
          <span className="font-medium text-gray-900">{source.title}</span>
          <span className="text-sm text-gray-500">
            {displayTotals.totalRecords} {t('onFarmImplant.sourceCard.records')}
          </span>
          <span className="text-sm text-gray-500">
            {displayTotals.totalEmbryos} {t('onFarmImplant.sourceCard.embryos')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isSelectingAll && (
            <i className="pi pi-spin pi-spinner text-xs text-gray-400" />
          )}
          <Checkbox
            aria-label={t('onFarmImplant.sourceCard.selectAll')}
            checked={allSelected}
            indeterminate={someSelected}
            disabled={readOnly || isSelectingAll || showClientOnFarmSearchHint}
            onCheckedChange={(checked) => {
              void handleSelectAllChange(checked);
            }}
          />
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.sourceCard.selectAll')}
          </span>
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.sourceCard.selectedCount', {
              selected: selectedCount,
              total: selectableTotal,
            })}
          </span>
          <Button
            variant="ghost"
            size="icon-xs"
            disabled={readOnly}
            onClick={() => onRemove(source.sourceId)}
          >
            <i className="pi pi-times" />
          </Button>
        </div>
      </div>

      <div className="px-4 py-2">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative w-48">
              <i className="pi pi-search absolute top-1/2 left-2 -translate-y-1/2 text-xs text-gray-400" />
              <Input
                placeholder={t(
                  isClientOnFarm
                    ? 'onFarmImplant.sourceCard.searchCanePlaceholder'
                    : 'onFarmImplant.sourceCard.filterRecordsPlaceholder',
                )}
                className="h-7 pl-7 text-xs"
                disabled={isClientOnFarm && showSelectedOnly}
                value={searchText}
                onChange={handleSearchChange}
              />
              {isLoading && (
                <i className="pi pi-spin pi-spinner absolute top-1/2 right-2 -translate-y-1/2 text-xs text-gray-400" />
              )}
            </div>
            {supportsShipments && !readOnly && (
              <ShipmentPicker
                sourceType={isFrozen ? 'frozen' : 'fresh'}
                selectedRecordKeys={selectedKeySet}
                embryoPickKeys={embryoPickKeys}
                onAdd={handleAddShipment}
              />
            )}
            {supportsShipments && (
              <ShipmentsInSelection
                sourceType={isFrozen ? 'frozen' : 'fresh'}
                shipments={shipments}
                readOnly={readOnly}
                onRemove={handleRemoveShipment}
              />
            )}
            {isClientOnFarm && (
              <>
                <div className="relative w-56">
                  <i className="pi pi-search absolute top-1/2 left-2 -translate-y-1/2 text-xs text-gray-400" />
                  <Input
                    aria-label={t(
                      'onFarmImplant.sourceCard.searchAccountPlaceholder',
                    )}
                    placeholder={t(
                      usesCurrentAccount
                        ? 'onFarmImplant.sourceCard.currentAccountPlaceholder'
                        : 'onFarmImplant.sourceCard.searchAccountPlaceholder',
                    )}
                    className="h-7 pl-7 text-xs"
                    disabled={usesCurrentAccount || showSelectedOnly}
                    value={
                      usesCurrentAccount
                        ? (accountName ?? '')
                        : accountSearchText
                    }
                    onChange={handleAccountSearchChange}
                  />
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <Checkbox
                    aria-label={t(
                      'onFarmImplant.sourceCard.filterByCurrentAccount',
                    )}
                    checked={usesCurrentAccount}
                    disabled={!accountId || showSelectedOnly}
                    onCheckedChange={handleFilterByCurrentAccountChange}
                  />
                  {t('onFarmImplant.sourceCard.filterByCurrentAccount')}
                </label>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <Checkbox
                aria-label={t('onFarmImplant.sourceCard.showSelectedOnly')}
                checked={showSelectedOnly}
                disabled={selectedCount === 0 && !showSelectedOnly}
                onCheckedChange={handleShowSelectedOnlyChange}
              />
              {t('onFarmImplant.sourceCard.showSelectedOnly')}
            </label>
            <span className="text-xs text-gray-500">
              {displayTotals.totalRecords}{' '}
              {t('onFarmImplant.sourceCard.records')}
            </span>
          </div>
        </div>

        {showClientOnFarmSearchHint && (
          <p className="mb-2 text-xs text-gray-500">
            {t('onFarmImplant.sourceCard.clientOnFarmSearchHint')}
          </p>
        )}

        <div
          className={clsx(
            `ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}`,
            isClientOnFarm ? 'h-[320px]' : 'h-[200px]',
          )}
        >
          <WithLocalStorage storageKey={`on-farm-implant-source-${source.id}`}>
            <ClientOnFarmSelectionProvider value={clientOnFarmSelection}>
              <FrozenPartialMatingsProvider value={partialMatings}>
                <AgGridReact
                  ref={gridRef}
                  gridOptions={gridOptions}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  headerHeight={36}
                  rowHeight={48}
                  rowSelection="multiple"
                  suppressRowClickSelection
                  getRowId={getRowId<MatingRecord>((params) =>
                    toRecordKey(params.data.id, params.data.accountId),
                  )}
                  getContextMenuItems={getContextMenuItems}
                  rowModelType="serverSide"
                  serverSideDatasource={dataSource}
                  cacheBlockSize={PAGE_SIZE}
                  blockLoadDebounceMillis={300}
                  onSelectionChanged={handleSelectionChanged}
                  onModelUpdated={applySelectionToGrid}
                  onStoreRefreshed={applySelectionToGrid}
                  {...masterDetailProps}
                />
              </FrozenPartialMatingsProvider>
            </ClientOnFarmSelectionProvider>
          </WithLocalStorage>
        </div>
      </div>
    </div>
  );
};
